import { useState, useEffect, useCallback } from "react";
import { useApi } from "../hooks/useApi";
import Sidebar from "../components/Sidebar";
import { Topbar, PageError, friendlyError } from "../components";
import { setStoredTimeout } from "../hooks/useSessionTimeout";

// ── Types ────────────────────────────────────────────────────────────────────

interface NotificationSettings {
  notifications_enabled: boolean;
  notify_email:          string | null;
  notify_on_success:     boolean;
  notify_on_failure:     boolean;
  notify_on_warning:     boolean;
  session_timeout_mins:  number;
}

interface TenantInfo {
  id: string;
  slug: string;
  name: string;
  plan: string;
  max_orgs: number;
  features: string[];
}

interface Usage {
  active_connectors: number;
  jobs_last_30d: number;
  total_bytes_all_time: number;
  active_users: number;
}

// ── Plan definitions ─────────────────────────────────────────────────────────

interface PlanDef {
  key: string;
  name: string;
  price: string;
  priceSub: string;
  storage: string;
  storageBytes: number;
  connectors: string;
  connectorsNum: number;
  users: string;
  usersNum: number;
  frequency: string;
  retention: string;
  encryption: boolean;
  mfa: boolean;
  emailNotifs: boolean;
  support: string;
}

const PLANS: PlanDef[] = [
  {
    key: "free", name: "Free", price: "Free", priceSub: "forever",
    storage: "1 GB", storageBytes: 1 * 1024 ** 3,
    connectors: "1", connectorsNum: 1,
    users: "1", usersNum: 1,
    frequency: "Daily", retention: "30 days",
    encryption: false, mfa: false, emailNotifs: false,
    support: "Community",
  },
  {
    key: "sme", name: "SME", price: "\u00A39", priceSub: "/month",
    storage: "10 GB", storageBytes: 10 * 1024 ** 3,
    connectors: "3", connectorsNum: 3,
    users: "5", usersNum: 5,
    frequency: "Every 6 hours", retention: "90 days",
    encryption: true, mfa: true, emailNotifs: true,
    support: "Email",
  },
  {
    key: "practice", name: "Practice", price: "\u00A329", priceSub: "/month",
    storage: "100 GB", storageBytes: 100 * 1024 ** 3,
    connectors: "10", connectorsNum: 10,
    users: "25", usersNum: 25,
    frequency: "Hourly", retention: "1 year",
    encryption: true, mfa: true, emailNotifs: true,
    support: "Priority",
  },
  {
    key: "enterprise", name: "Enterprise", price: "\u00A399", priceSub: "/month",
    storage: "1 TB", storageBytes: 1024 * 1024 ** 3,
    connectors: "Unlimited", connectorsNum: Infinity,
    users: "Unlimited", usersNum: Infinity,
    frequency: "Every 15 min", retention: "Unlimited",
    encryption: true, mfa: true, emailNotifs: true,
    support: "Dedicated",
  },
];

const TIMEOUT_OPTIONS = [5,10,15,20,25,30,45,60,90,120].map(m => ({
  value: m,
  label: m < 60 ? `${m} minutes` : m === 60 ? "1 hour" : `${m / 60} hours`,
}));

type Tab = "general" | "subscription";

// ── Main component ───────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { get, post, patch } = useApi();
  const [activeTab, setActiveTab] = useState<Tab>("general");

  // ── Tenant / usage data (for subscription tab) ───────────────────────────
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [usage, setUsage]   = useState<Usage | null>(null);

  useEffect(() => {
    get<{ tenant: TenantInfo; usage: Usage }>("/api/tenants/me")
      .then(r => { setTenant(r.tenant); setUsage(r.usage); })
      .catch(() => {});
  }, [get]);

  // ── MFA state ────────────────────────────────────────────────────────────
  const [mfaEnabled, setMfaEnabled]       = useState(false);
  const [mfaSetupUri, setMfaSetupUri]     = useState<string | null>(null);
  const [mfaSetupSecret, setMfaSetupSecret] = useState<string | null>(null);
  const [mfaCode, setMfaCode]             = useState("");
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState<string[] | null>(null);
  const [mfaDisableCode, setMfaDisableCode] = useState("");
  const [mfaLoading, setMfaLoading]       = useState(false);
  const [mfaError, setMfaError]           = useState("");
  const [mfaStep, setMfaStep]             = useState<"idle" | "setup" | "verify" | "recovery" | "disabling">("idle");

  useEffect(() => {
    get<{ mfa_enabled: boolean }>("/api/mfa/status")
      .then(r => setMfaEnabled(r.mfa_enabled))
      .catch(() => {});
  }, [get]);

  const startMfaSetup = useCallback(async () => {
    setMfaLoading(true); setMfaError("");
    try {
      const res = await post<{ otpauth_uri: string; secret: string }>("/api/mfa/setup", {});
      setMfaSetupUri(res.otpauth_uri);
      setMfaSetupSecret(res.secret);
      setMfaStep("setup");
    } catch (err: any) {
      setMfaError(err.message ?? "Failed to start MFA setup");
    } finally { setMfaLoading(false); }
  }, [post]);

  const verifyMfaSetup = useCallback(async () => {
    setMfaLoading(true); setMfaError("");
    try {
      const res = await post<{ enabled: boolean; recovery_codes: string[] }>("/api/mfa/verify-setup", { code: mfaCode });
      setMfaEnabled(true);
      setMfaRecoveryCodes(res.recovery_codes);
      setMfaStep("recovery");
    } catch (err: any) {
      setMfaError(err.message ?? "Invalid code");
    } finally { setMfaLoading(false); }
  }, [post, mfaCode]);

  const disableMfa = useCallback(async () => {
    setMfaLoading(true); setMfaError("");
    try {
      await post<{ disabled: boolean }>("/api/mfa/disable", { code: mfaDisableCode });
      setMfaEnabled(false);
      setMfaStep("idle");
      setMfaDisableCode("");
    } catch (err: any) {
      setMfaError(err.message ?? "Invalid code");
    } finally { setMfaLoading(false); }
  }, [post, mfaDisableCode]);

  // ── Notification settings state ──────────────────────────────────────────
  const [settings, setSettings] = useState<NotificationSettings>({
    notifications_enabled: false,
    notify_email:          null,
    notify_on_success:     false,
    notify_on_failure:     true,
    notify_on_warning:     true,
    session_timeout_mins:  30,
  });
  const [emailInput, setEmailInput] = useState("");
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState("");
  const [pageError, setPageError]   = useState<string | null>(null);

  useEffect(() => {
    get<{ settings: NotificationSettings }>("/api/settings")
      .then(r => {
        setSettings(r.settings);
        setEmailInput(r.settings.notify_email ?? "");
      })
      .catch(err => setPageError(friendlyError(err)))
      .finally(() => setLoading(false));
  }, [get]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      const res = await patch<{ settings: NotificationSettings }>("/api/settings", {
        ...settings,
        notify_email: emailInput.trim() || null,
      });
      setSettings(res.settings);
      setEmailInput(res.settings.notify_email ?? "");
      setStoredTimeout(res.settings.session_timeout_mins);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.message ?? "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const toggle = (field: keyof NotificationSettings) =>
    setSettings(s => ({ ...s, [field]: !s[field] }));

  return (
    <div className="shell">
      <Sidebar />
      <div className="main-column">
        <Topbar title="Tenant Settings" subtitle="Manage your plan, notifications, and preferences" />
        <main className="page">
          {pageError && <PageError message={pageError} onRetry={() => window.location.reload()} />}

          {!pageError && (loading ? (
            <div style={{ textAlign: "center", color: "var(--text3)", padding: "60px 0" }}>Loading...</div>
          ) : (
            <>
              {/* ── Tab bar ──────────────────────────────────── */}
              <div style={{
                display: "flex", gap: 0, marginBottom: 24, maxWidth: 600,
                borderBottom: "1px solid var(--border)",
              }}>
                <TabButton active={activeTab === "general"} onClick={() => setActiveTab("general")}>General</TabButton>
                <TabButton active={activeTab === "subscription"} onClick={() => setActiveTab("subscription")}>Subscription</TabButton>
              </div>

              {/* ── General tab ──────────────────────────────── */}
              {activeTab === "general" && (
                <form onSubmit={handleSave} style={{ maxWidth: 600 }}>

                  {/* Email notifications card */}
                  <div className="card" style={{ padding: 24, marginBottom: 24 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20 }}>
                      <div>
                        <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>Email notifications</h3>
                        <p style={{ margin: 0, fontSize: 12, color: "var(--text3)" }}>
                          Receive emails when backups complete, fail, or produce warnings.
                        </p>
                      </div>
                      <label style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 8, flexShrink: 0, marginLeft: 16 }}>
                        <span style={{ fontSize: 12, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                          {settings.notifications_enabled ? "On" : "Off"}
                        </span>
                        <div
                          onClick={() => toggle("notifications_enabled")}
                          style={{
                            width: 36, height: 20, borderRadius: 10, cursor: "pointer",
                            background: settings.notifications_enabled ? "var(--teal)" : "var(--border2)",
                            position: "relative", transition: "background 0.15s",
                          }}
                        >
                          <div style={{
                            position: "absolute", top: 2,
                            left: settings.notifications_enabled ? 18 : 2,
                            width: 16, height: 16, borderRadius: "50%",
                            background: "#fff", transition: "left 0.15s",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          }} />
                        </div>
                      </label>
                    </div>

                    {settings.notifications_enabled && (
                      <>
                        {/* Notification email */}
                        <div style={{ marginBottom: 20 }}>
                          <label style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            Send notifications to
                          </label>
                          <input
                            type="email"
                            value={emailInput}
                            onChange={e => setEmailInput(e.target.value)}
                            placeholder="Leave blank to use the admin account email"
                            className="form-input"
                            style={{ width: "100%" }}
                          />
                          <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--text3)" }}>
                            If left blank, notifications are sent to the first admin user's email.
                          </p>
                        </div>

                        {/* Event checkboxes */}
                        <div>
                          <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            Notify me on
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <CheckRow
                              checked={settings.notify_on_failure}
                              onChange={() => toggle("notify_on_failure")}
                              label="Backup failures"
                              description="Email when a backup job fails"
                              accent="var(--red, #ef4444)"
                            />
                            <CheckRow
                              checked={settings.notify_on_warning}
                              onChange={() => toggle("notify_on_warning")}
                              label="Warnings"
                              description="Email when a backup completes with warnings"
                              accent="#F59E0B"
                            />
                            <CheckRow
                              checked={settings.notify_on_success}
                              onChange={() => toggle("notify_on_success")}
                              label="Successful backups"
                              description="Email every time a backup completes successfully"
                              accent="var(--teal)"
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Two-factor authentication card */}
                  <div className="card" style={{ padding: 24, marginBottom: 24 }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 12 }}>
                      <div>
                        <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>Two-factor authentication</h3>
                        <p style={{ margin: 0, fontSize: 12, color: "var(--text3)" }}>
                          Add an extra layer of security using an authenticator app.
                        </p>
                      </div>
                      {mfaEnabled && mfaStep === "idle" && (
                        <span style={{ fontSize: 11, fontFamily: "var(--mono)", background: "rgba(13,148,136,0.12)", color: "var(--teal)", padding: "2px 8px", borderRadius: "var(--r)", flexShrink: 0 }}>
                          Enabled
                        </span>
                      )}
                    </div>

                    {mfaError && (
                      <div style={{ background: "var(--red-bg)", color: "#791F1F", fontSize: 13, padding: "10px 14px", borderRadius: "var(--r)", marginBottom: 12 }}>{mfaError}</div>
                    )}

                    {/* Idle — not enabled */}
                    {!mfaEnabled && mfaStep === "idle" && (
                      <button className="btn btn-primary" onClick={startMfaSetup} disabled={mfaLoading} style={{ fontSize: 13 }}>
                        {mfaLoading ? "Setting up..." : "Enable two-factor authentication"}
                      </button>
                    )}

                    {/* Step 1: Scan QR / enter key */}
                    {mfaStep === "setup" && mfaSetupUri && (
                      <div>
                        <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 12 }}>
                          Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator, Authy, 1Password, etc.):
                        </p>
                        <div style={{ background: "#fff", borderRadius: 8, padding: 12, display: "inline-block", marginBottom: 12 }}>
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(mfaSetupUri)}`}
                            alt="Scan with authenticator app"
                            width={180} height={180}
                            style={{ display: "block" }}
                          />
                        </div>
                        <div style={{ marginBottom: 16 }}>
                          <p style={{ fontSize: 11, color: "var(--text3)", marginBottom: 4 }}>Or enter this key manually:</p>
                          <code style={{
                            display: "block", background: "var(--surface2)", border: "1px solid var(--border)",
                            borderRadius: "var(--r)", padding: "8px 12px", fontSize: 13, fontFamily: "var(--mono)",
                            letterSpacing: "0.1em", wordBreak: "break-all", color: "var(--text)",
                          }}>
                            {mfaSetupSecret}
                          </code>
                        </div>
                        <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 8 }}>
                          Enter the 6-digit code from your authenticator to confirm:
                        </p>
                        <div style={{ display: "flex", gap: 8 }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            placeholder="123456"
                            value={mfaCode}
                            onChange={e => setMfaCode(e.target.value)}
                            className="form-input"
                            style={{ width: 140, fontSize: 16, fontFamily: "var(--mono)", textAlign: "center", letterSpacing: "0.15em" }}
                          />
                          <button className="btn btn-primary" onClick={verifyMfaSetup} disabled={mfaLoading || mfaCode.length < 6} style={{ fontSize: 13 }}>
                            {mfaLoading ? "Verifying..." : "Verify & enable"}
                          </button>
                          <button className="btn btn-ghost" onClick={() => { setMfaStep("idle"); setMfaError(""); }} style={{ fontSize: 13 }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Step 2: Recovery codes — shown once after enabling */}
                    {mfaStep === "recovery" && mfaRecoveryCodes && (
                      <div>
                        <div style={{ background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.2)", borderRadius: "var(--r)", padding: 16, marginBottom: 16 }}>
                          <p style={{ fontSize: 13, fontWeight: 600, color: "#D97706", margin: "0 0 8px" }}>
                            Save your recovery codes
                          </p>
                          <p style={{ fontSize: 12, color: "var(--text2)", margin: "0 0 12px" }}>
                            Store these codes in a safe place. Each code can only be used once to sign in if you lose access to your authenticator app.
                          </p>
                          <div style={{
                            display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6,
                            background: "var(--surface)", border: "1px solid var(--border)",
                            borderRadius: "var(--r)", padding: 12,
                          }}>
                            {mfaRecoveryCodes.map((code, i) => (
                              <code key={i} style={{ fontSize: 13, fontFamily: "var(--mono)", color: "var(--text)", padding: "2px 0" }}>
                                {code}
                              </code>
                            ))}
                          </div>
                        </div>
                        <button
                          className="btn btn-primary"
                          onClick={() => { setMfaStep("idle"); setMfaRecoveryCodes(null); setMfaCode(""); setMfaError(""); }}
                          style={{ fontSize: 13 }}
                        >
                          I've saved my recovery codes
                        </button>
                      </div>
                    )}

                    {/* Enabled — option to disable */}
                    {mfaEnabled && mfaStep === "idle" && (
                      <div style={{ marginTop: 12 }}>
                        <button
                          className="btn btn-ghost"
                          onClick={() => { setMfaStep("disabling"); setMfaError(""); }}
                          style={{ fontSize: 12, color: "var(--text3)" }}
                        >
                          Disable two-factor authentication
                        </button>
                      </div>
                    )}

                    {/* Disable confirmation */}
                    {mfaStep === "disabling" && (
                      <div style={{ marginTop: 12 }}>
                        <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 8 }}>
                          Enter a code from your authenticator or a recovery code to disable MFA:
                        </p>
                        <div style={{ display: "flex", gap: 8 }}>
                          <input
                            type="text"
                            inputMode="numeric"
                            placeholder="123456 or XXXX-XXXX"
                            value={mfaDisableCode}
                            onChange={e => setMfaDisableCode(e.target.value)}
                            className="form-input"
                            style={{ width: 180, fontSize: 14, fontFamily: "var(--mono)", textAlign: "center" }}
                          />
                          <button className="btn btn-primary" onClick={disableMfa} disabled={mfaLoading || !mfaDisableCode.trim()} style={{ fontSize: 13 }}>
                            {mfaLoading ? "Disabling..." : "Disable MFA"}
                          </button>
                          <button className="btn btn-ghost" onClick={() => { setMfaStep("idle"); setMfaError(""); setMfaDisableCode(""); }} style={{ fontSize: 13 }}>
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Session timeout card */}
                  <div className="card" style={{ padding: 24, marginBottom: 24 }}>
                    <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>Session timeout</h3>
                    <p style={{ margin: "0 0 16px", fontSize: 12, color: "var(--text3)" }}>
                      Automatically log out inactive users after a set period.
                    </p>
                    <div>
                      <label style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Log out after
                      </label>
                      <select
                        value={settings.session_timeout_mins}
                        onChange={e => setSettings(s => ({ ...s, session_timeout_mins: parseInt(e.target.value, 10) }))}
                        className="form-input"
                        style={{ width: 200 }}
                      >
                        {TIMEOUT_OPTIONS.map(o => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      <p style={{ margin: "6px 0 0", fontSize: 11, color: "var(--text3)" }}>
                        Applies to all users in this tenant. A warning is shown 60 seconds before logout.
                      </p>
                    </div>
                  </div>

                  {error && (
                    <div style={{
                      background: "var(--red-bg)", color: "#791F1F", fontSize: 13,
                      padding: "10px 14px", borderRadius: "var(--r)", marginBottom: 16,
                    }}>{error}</div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <button type="submit" className="btn btn-primary" disabled={saving}>
                      {saving ? "Saving..." : "Save settings"}
                    </button>
                    {saved && (
                      <span style={{ fontSize: 13, color: "var(--teal)", fontFamily: "var(--mono)" }}>
                        Saved
                      </span>
                    )}
                  </div>

                </form>
              )}

              {/* ── Subscription tab ─────────────────────────── */}
              {activeTab === "subscription" && tenant && usage && (
                <SubscriptionTab tenant={tenant} usage={usage} />
              )}
            </>
          ))}
        </main>
      </div>
    </div>
  );
}

// ── Tab button ───────────────────────────────────────────────────────────────

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "10px 20px",
        fontSize: 13,
        fontWeight: 500,
        fontFamily: "var(--sans)",
        color: active ? "var(--text)" : "var(--text3)",
        background: "transparent",
        border: "none",
        borderBottom: active ? "2px solid var(--teal)" : "2px solid transparent",
        cursor: "pointer",
        transition: "all 0.15s",
      }}
    >
      {children}
    </button>
  );
}

// ── Subscription tab ─────────────────────────────────────────────────────────

function SubscriptionTab({ tenant, usage }: { tenant: TenantInfo; usage: Usage }) {
  const currentPlan = PLANS.find(p => p.key === tenant.plan) ?? PLANS[0];

  return (
    <div style={{ maxWidth: 800 }}>

      {/* Current plan summary */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>Current plan</h3>
            <p style={{ margin: 0, fontSize: 12, color: "var(--text3)" }}>
              You are on the <strong style={{ color: "var(--text)" }}>{currentPlan.name}</strong> plan.
            </p>
          </div>
          <div style={{
            background: tenant.plan === "free" ? "var(--surface2)" : "rgba(13,148,136,0.12)",
            color: tenant.plan === "free" ? "var(--text3)" : "var(--teal)",
            fontSize: 13, fontWeight: 600, fontFamily: "var(--mono)",
            padding: "6px 14px", borderRadius: "var(--r)",
          }}>
            {currentPlan.price === "Free" ? "Free" : `${currentPlan.price}${currentPlan.priceSub}`}
          </div>
        </div>

        {/* Usage meters */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <UsageMeter
            label="Connectors"
            used={usage.active_connectors}
            limit={currentPlan.connectorsNum}
            formatVal={(v) => String(v)}
            formatLimit={(v) => v === Infinity ? "Unlimited" : String(v)}
          />
          <UsageMeter
            label="Storage"
            used={usage.total_bytes_all_time}
            limit={currentPlan.storageBytes}
            formatVal={formatBytes}
            formatLimit={(v) => v === Infinity ? "Unlimited" : formatBytes(v)}
          />
          <UsageMeter
            label="Team members"
            used={usage.active_users}
            limit={currentPlan.usersNum}
            formatVal={(v) => String(v)}
            formatLimit={(v) => v === Infinity ? "Unlimited" : String(v)}
          />
          <UsageMeter
            label="Backups (30d)"
            used={usage.jobs_last_30d}
            limit={Infinity}
            formatVal={(v) => String(v)}
            formatLimit={() => "Unlimited"}
          />
        </div>
      </div>

      {/* Plan features */}
      <div className="card" style={{ padding: 24, marginBottom: 24 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>Your plan includes</h3>
        <p style={{ margin: "0 0 16px", fontSize: 12, color: "var(--text3)" }}>
          Features available on the {currentPlan.name} plan.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <FeatureItem label="Backup frequency" value={currentPlan.frequency} />
          <FeatureItem label="Data retention" value={currentPlan.retention} />
          <FeatureItem label="Encryption at rest" value={currentPlan.encryption ? "Yes" : "No"} positive={currentPlan.encryption} />
          <FeatureItem label="Two-factor auth" value={currentPlan.mfa ? "Yes" : "No"} positive={currentPlan.mfa} />
          <FeatureItem label="Email notifications" value={currentPlan.emailNotifs ? "Yes" : "No"} positive={currentPlan.emailNotifs} />
          <FeatureItem label="Support" value={currentPlan.support} />
        </div>
      </div>

      {/* Plan comparison */}
      <div className="card" style={{ padding: 24 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600 }}>Compare plans</h3>
        <p style={{ margin: "0 0 20px", fontSize: 12, color: "var(--text3)" }}>
          Choose the plan that fits your needs. Upgrade or downgrade at any time.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {PLANS.map(plan => {
            const isCurrent = plan.key === tenant.plan;
            const isUpgrade = PLANS.indexOf(plan) > PLANS.findIndex(p => p.key === tenant.plan);
            return (
              <div
                key={plan.key}
                style={{
                  border: isCurrent ? "2px solid var(--teal)" : "1px solid var(--border)",
                  borderRadius: "var(--r2)",
                  padding: 16,
                  position: "relative",
                  background: isCurrent ? "rgba(13,148,136,0.04)" : "var(--surface)",
                }}
              >
                {isCurrent && (
                  <div style={{
                    position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
                    background: "var(--teal)", color: "#fff",
                    fontSize: 10, fontWeight: 600, fontFamily: "var(--mono)",
                    padding: "2px 10px", borderRadius: 10,
                    textTransform: "uppercase", letterSpacing: "0.05em",
                  }}>
                    Current
                  </div>
                )}

                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>{plan.name}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text)" }}>
                    {plan.price}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text3)", fontFamily: "var(--mono)" }}>
                    {plan.priceSub}
                  </div>
                </div>

                <div style={{ fontSize: 12, color: "var(--text2)", display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                  <PlanRow label="Storage" value={plan.storage} />
                  <PlanRow label="Connectors" value={plan.connectors} />
                  <PlanRow label="Users" value={plan.users} />
                  <PlanRow label="Frequency" value={plan.frequency} />
                  <PlanRow label="Retention" value={plan.retention} />
                  <PlanRow label="Support" value={plan.support} />
                </div>

                {isCurrent ? (
                  <div style={{
                    textAlign: "center", fontSize: 12, color: "var(--text3)",
                    padding: "8px 0", fontFamily: "var(--mono)",
                  }}>
                    Active
                  </div>
                ) : (
                  <button
                    className={isUpgrade ? "btn btn-primary" : "btn btn-ghost"}
                    style={{ width: "100%", fontSize: 12, padding: "8px 0" }}
                    onClick={() => alert(`Billing integration coming soon.\n\nTo ${isUpgrade ? "upgrade" : "change"} to the ${plan.name} plan, please contact support.`)}
                  >
                    {isUpgrade ? "Upgrade" : "Downgrade"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function UsageMeter({
  label, used, limit, formatVal, formatLimit,
}: {
  label: string;
  used: number;
  limit: number;
  formatVal: (v: number) => string;
  formatLimit: (v: number) => string;
}) {
  const pct = limit === Infinity ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const isHigh = pct >= 80;
  const barColor = isHigh ? "var(--red, #ef4444)" : "var(--teal)";

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          {label}
        </span>
        <span style={{ fontSize: 12, fontFamily: "var(--mono)", color: "var(--text2)" }}>
          {formatVal(used)} / {formatLimit(limit)}
        </span>
      </div>
      <div style={{
        height: 6, borderRadius: 3,
        background: "var(--border)",
        overflow: "hidden",
      }}>
        <div style={{
          height: "100%", borderRadius: 3,
          width: limit === Infinity ? "2%" : `${Math.max(2, pct)}%`,
          background: limit === Infinity ? "var(--teal)" : barColor,
          transition: "width 0.3s",
        }} />
      </div>
    </div>
  );
}

function FeatureItem({ label, value, positive }: { label: string; value: string; positive?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
      <span style={{ fontSize: 12, color: "var(--text3)" }}>{label}</span>
      <span style={{
        fontSize: 12, fontWeight: 500, fontFamily: "var(--mono)",
        color: positive === false ? "var(--text3)" : positive === true ? "var(--teal)" : "var(--text2)",
      }}>
        {value}
      </span>
    </div>
  );
}

function PlanRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between" }}>
      <span style={{ color: "var(--text3)" }}>{label}</span>
      <span style={{ fontWeight: 500, fontFamily: "var(--mono)", color: "var(--text)" }}>{value}</span>
    </div>
  );
}

function CheckRow({
  checked, onChange, label, description, accent,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
  description: string;
  accent: string;
}) {
  return (
    <label style={{ display: "flex", alignItems: "flex-start", gap: 12, cursor: "pointer" }}>
      <div style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0, marginTop: 1,
        border: `2px solid ${checked ? accent : "var(--border2)"}`,
        background: checked ? accent : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.1s",
      }}
        onClick={onChange}
      >
        {checked && (
          <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
            <path d="M1 3.5L3.5 6L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <div onClick={onChange}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text)" }}>{label}</div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 1 }}>{description}</div>
      </div>
    </label>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
